import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { Fieldset } from '@antimatterhq/ui/components/fieldset';
import { Badge } from '@antimatterhq/ui/components/badge';
import Spacer from '@antimatterhq/ui/components/spacer';
import {
  CustomNotebookFunctionReview,
  FieldSpecReview,
  FiltersReview,
} from '../';
import { getFieldTableRows, getFieldSchemaValues } from '@/lib/datasource';
import { useGetDatasourcePreset } from '@/hooks/content/useGetDatasourcePreset';
import { filterNulls } from '@/utils/filterNulls';

export type GoldReviewProps = {
  dataSource: core_v1_DataSource;
  presetVersion?: number;
};

export const GoldReview = ({ dataSource, presetVersion }: GoldReviewProps) => {
  const { data: presets } = useGetDatasourcePreset({
    name: dataSource.spec.usePreset || undefined,
    version: presetVersion || dataSource.spec.usePresetVersion,
  });

  const addTables = dataSource.spec.gold?.presetOverrides?.addTables?.map(
    ({ fields, ...values }) => ({
      ...values,
      fields: getFieldTableRows(
        undefined,
        getFieldSchemaValues(filterNulls(fields)),
        undefined,
      ),
    }),
  );

  const modifyTables = presets?.gold?.map((preset) => {
    const { addFields, omitFields, name, ...modifyTable } =
      dataSource.spec.gold?.presetOverrides?.modifyTables?.find(
        (q) => q.name === preset.name,
      ) || {};

    const fields = getFieldTableRows(
      filterNulls(preset.fields),
      getFieldSchemaValues(filterNulls(addFields)),
      filterNulls(omitFields),
    );

    return {
      ...modifyTable,
      name: name || '',
      fields,
    };
  });

  const omitTables = dataSource.spec.gold?.presetOverrides?.omitTables || [];

  if (
    (!modifyTables || modifyTables?.length === 0) &&
    (!addTables || addTables?.length === 0)
  ) {
    return (
      <div>
        <div>
          <Fieldset.Field>
            <Fieldset.Label>No gold tables</Fieldset.Label>
          </Fieldset.Field>
        </div>
      </div>
    );
  }

  return (
    <div>
      {modifyTables?.map((modifyTable) => (
        <div key={modifyTable.name}>
          <Badge
            variant="filled"
            color="gold"
            data-testid={`review-gold-table-${modifyTable.name}-badge`}
          >
            {modifyTable.name}
          </Badge>

          <Spacer size="lg" />

          {omitTables.includes(modifyTable.name) && (
            <div key={modifyTable.name}>
              <Fieldset.Field>
                <Fieldset.Label>Disabled</Fieldset.Label>
              </Fieldset.Field>
            </div>
          )}

          {modifyTable.custom?.function && (
            <CustomNotebookFunctionReview custom={modifyTable.custom} />
          )}

          {!omitTables.includes(modifyTable.name) &&
            !modifyTable.custom?.function && (
              <>
                <div>
                  <Fieldset.Field>
                    <Fieldset.Label>Source table:</Fieldset.Label>
                    <Fieldset.Value>{modifyTable.sourceTable}</Fieldset.Value>
                  </Fieldset.Field>
                </div>

                <Spacer size="lg" />

                <div>
                  <Fieldset.Field>
                    <Fieldset.Label>Field Summary:</Fieldset.Label>
                  </Fieldset.Field>
                  <Fieldset.List>
                    <FieldSpecReview fields={modifyTable.fields} />
                  </Fieldset.List>
                </div>

                <Spacer size="lg" />

                <FiltersReview
                  filter={modifyTable.filter || ''}
                  postFilter={modifyTable.postFilter || ''}
                />

                <Spacer size="2xl" />
              </>
            )}
        </div>
      ))}

      {addTables?.map((addTable) => (
        <div key={addTable.name}>
          <Badge
            variant="filled"
            color="gold"
            data-testid={`review-gold-table-${addTable.name}-badge`}
          >
            {addTable.name}
          </Badge>

          <Spacer size="lg" />

          {addTable.custom?.function && (
            <CustomNotebookFunctionReview custom={addTable.custom} />
          )}

          {!addTable.custom?.function && (
            <>
              <div key={addTable.name}>
                <Fieldset.Field>
                  <Fieldset.Label>Field Summary:</Fieldset.Label>
                </Fieldset.Field>
                <Fieldset.List>
                  <FieldSpecReview fields={addTable.fields} />
                </Fieldset.List>
              </div>

              <Spacer size="lg" />

              <FiltersReview
                filter={addTable.filter || ''}
                postFilter={addTable.postFilter || ''}
              />

              <Spacer size="2xl" />
            </>
          )}
        </div>
      ))}
    </div>
  );
};
